import React, { useEffect } from "react";
import { Globals, Reset } from "@icecreamsocial/components";
import ThemeProvider from "../../providers/ThemeProvider";
import { Main } from "../../views/Main";
import { Deeplinker } from "./Deeplinker";
import { Snackbar } from "../../presentational";
import { FlavorLayout } from "..";
import { usePostMessage } from "../../hooks";
import { Localizer } from "./Localizer";
import { usePostHog } from "posthog-js/react";
import { getPersistedUser, persistUserConsent } from "../../../utils";
import { useAtom, useSetAtom } from "jotai";
import {
  campaignIdAtom,
  publicKeyAtom,
  flavorAtom,
  openAtom,
  engagementAtom,
  variablesAtom,
  configAtom,
  behaviorAtom,
  ExtendedConfig,
  trackConversionAtom,
  transactionConversionTrackedAtom,
  persistUserAtom,
  userIdAtom,
  campaignStatusAtom
} from "../../../shared/atoms";

function trackOnce(posthog, config: Partial<ExtendedConfig>) {
  const { data, behavior = {}, variables = {} } = config;
  /**
   * will be sent up with any further `posthog.capture()` events
   */
  const trackOnce = {
    campaignId: data.campaignId,
    loadedOn: data.widgetLocation,
    hasInvite: !!data.invite,
    flavor: behavior.flavor,
    engagement: data.engagement,
    subtotal: data.subtotal || data.revenue,
    productName: variables.productName,
    productURL: variables.productUrl,
    promocode: variables.promocode
  };

  posthog?.register(trackOnce);
  posthog?.people.set_once(trackOnce);
}

const Initializer = () => {
  // atomic state
  const setCampaignId = useSetAtom(campaignIdAtom);
  const setPublicKey = useSetAtom(publicKeyAtom);
  const [campaignStatus] = useAtom(campaignStatusAtom);
  const [flavor, setFlavor] = useAtom(flavorAtom);
  const setOpen = useSetAtom(openAtom);
  const setEngagement = useSetAtom(engagementAtom);
  const setVariables = useSetAtom(variablesAtom);
  const [config, setConfig] = useAtom(configAtom);
  const [behavior] = useAtom(behaviorAtom);
  const [, trackConversion] = useAtom(trackConversionAtom);
  const [transactionTracked, setTransactionTracked] = useAtom(
    transactionConversionTrackedAtom
  );
  const [persistedUser] = useAtom(persistUserAtom);
  const [userId, setUserId] = useAtom(userIdAtom);

  const posthog = usePostHog();

  const respondToParent = usePostMessage(e => {
    try {
      if (e.origin !== e.data?.sender) return;

      const { data } = e;
      const handlers = {
        ics_open: handleOpen,
        ics_init: handleInit,
        ics_serve: handleServe
      };
      const handler =
        handlers[data.type] ||
        function() {
          return;
        }; // noop on unknown command

      if (data.type !== "ics_resize") {
        posthog?.capture(data.type, data.data);
      }

      // this is a temporary check for how nested data is
      // i will get rid of this once the sdk is standardized in how
      // it dispatches messages
      const event = data.data?.data ? data.data : data;

      /**
       * persist the influencer on the widget side
       */
      if (!userId && event._uid) {
        setUserId(event._uid);
      }

      /**
       * delegate to event handlers
       */
      handler(event);

      // console.groupEnd('Iframe');
    } catch (error) {
      console.debug("error");
      console.error(error);
      throw error;
    }
  });

  // this is needed in case the widget is deployed using ICS.serve.  data might not have been provided during init so conversions
  // may not have been tracked
  const handleConversion = async ({ data }) => {
    /**
     * only track this conversion if there is an invite to attribute it to
     */
    if (data?.invite && !transactionTracked) {
      const conversionType =
        (data?.subtotal || data?.revenue) && data?.orderId
          ? "transaction"
          : "pageview";

      const user = await trackConversion([
        {
          email: persistedUser.email || data?.email || "guest",
          name: persistedUser.email || data?.email || "guest",
          identifier: persistedUser.email || data?.email || "guest",
          conversionType: data?.engagment || conversionType,
          orderId: data?.orderId,
          revenue: data?.subtotal || data?.revenue,
          inviteId: data?.invite,
          campaignId: data?.campaignId,
          transactionId: data?.transactionId,
          influencerId: userId
        }
      ]);

      setTransactionTracked(conversionType === "transaction");
    }
  };

  /**
   * opens the widget with updated config values
   */
  const handleServe = ({
    data: dataLayer,
    behavior,
    variables
  }: Partial<ExtendedConfig>) => {
    const data = { ...config.data, ...dataLayer };
    setFlavor(behavior?.flavor || flavor);
    setConfig({
      ...config,
      data,
      behavior: { ...config.behavior, ...behavior },
      variables: { ...config.variables, ...variables }
    });
    setOpen(true);
    // should be able to handle this async
    handleConversion({ data });
  };

  // might want to track here
  const handleOpen = ({ data }) => {
    const { clicked } = data;
    setOpen(true);
  };

  const handleInit = ({
    data,
    behavior,
    theme,
    variables,
    locale
  }: Partial<ExtendedConfig>) => {
    setConfig({ data, behavior, theme, variables, locale });
    setCampaignId(data.campaignId);
    setPublicKey(data.publicKey);
    setFlavor(behavior?.flavor || flavor);
    setEngagement(data?.engagement);
    // setVariables(variables || {});

    persistUserConsent("email", data?.email, getPersistedUser().emailConsent);
    persistUserConsent("sms", data?.mobileNumber || data?.phoneNumber, false);

    handleConversion({ data });

    trackOnce(posthog, { data, behavior, theme, variables, locale });
  };

  useEffect(() => {
    respondToParent({
      type: "ics_ready"
    });
  }, []);

  useEffect(() => {
    if (campaignStatus.isSuccess) {
      respondToParent({
        type: "ics_init_success"
      });
    }
  }, [campaignStatus]);

  return (
    <ThemeProvider theme={config?.theme}>
      <Localizer locale={config?.locale}>
        <Deeplinker />
        <Globals />
        <Reset />
        <Snackbar>
          <FlavorLayout>
            <Main />
          </FlavorLayout>
        </Snackbar>
      </Localizer>
    </ThemeProvider>
  );
};
export default Initializer;
